/**
 * Liquid Glass: every floating control of the app.
 *
 * Over the plan it is drawn by the GPU (`glassgl.ts`): the plan under it refracted at its rim, frosted,
 * lit from straight above — kept up with the plan frame by frame (the editor says when it has drawn:
 * `refreshGlass`) and with the control as it moves, grows, scales and fades.
 *
 * THE RULE OF THE CANVAS (2026-10-24, Adam: popups "work the first time but not after that"): each
 * control that is drawn glass has ONE WebGL canvas of its own, its first child, put in once — when the
 * plan arrives, or when the control is made, before it is ever shown — and never taken out, moved to
 * another control or put back while the control lives. Safari on a Mac stopped showing a control whose
 * canvas had been taken out and put back as it faded in (it showed only as it faded out). So a control
 * hidden keeps its canvas; one shown is drawn before its first frame; its words are never replaced by
 * wiping all it holds (`clear`, the groups' fills and the message pill keep the canvas). Safari keeps
 * sixteen WebGL contexts: fourteen at most are made; a control beyond them, the passing ones (menus,
 * popovers, sheets, the field pad, which moves between a sheet and the page), the library and pages with
 * no plan under them, and an inspector who has asked for less transparency or more contrast have CSS
 * glass (app.css) in the same tint and the same light.
 *
 * Safari blurs what lies behind a CSS glass control when the control itself changes, and not when only
 * what is behind it does: so every frame the plan is drawn each CSS glass control is nudged by a
 * change no one can see (`--gn`: a contrast of 100 % or 100.1 % at the end of its backdrop filter).
 */
import { Capture, GlassCanvas, type GlassLook, type PaneGeom, type Region } from './glassgl.ts';

const tracked = new Set<HTMLElement>();
/** The passing controls (menus, popovers, sheets, the field pad): CSS glass only. */
const cssOnly = new WeakSet<HTMLElement>();
let flip = false;

/**
 * A glass control to keep up with the plan under it (until the returned function is called). `gpu: false`:
 * CSS glass only — for a control that comes and goes, or moves between parents.
 */
export function trackGlass(el: HTMLElement, opts: { gpu?: boolean } = {}): () => void {
  tracked.add(el);
  if (opts.gpu === false) cssOnly.add(el);
  else if (live) attach(el);
  return () => {
    tracked.delete(el);
    detach(el);
  };
}

/** The plan was drawn again: every glass control shows it afresh, at once. */
export function refreshGlass(): void {
  if (live && source) {
    scene++;
    const t0 = performance.now();
    drawAll();
    if (import.meta.env.DEV) {
      stats.push([0, performance.now() - t0]);
      if (stats.length > 240) stats.shift();
    }
  }
  // (CSS glass — every control while the GPU's is not drawn, and any that found no canvas free — nudged)
  flip = !flip;
  const v = flip ? '1.001' : '1';
  for (const el of tracked) if (el.isConnected && !el.hidden && !el.classList.contains('gl')) el.style.setProperty('--gn', v);
}

/** What lies under the glass: the editor's layers (back to front) and its backdrop — and whether they show what is on screen yet. */
export interface GlassSource {
  layers(): HTMLCanvasElement[];
  backdrop(): string;
  ready(): boolean;
}
let source: GlassSource | null = null;
/** The plan's layers to draw the glass from (null: there is no plan under the glass now). */
export function setGlassSource(s: GlassSource | null): void {
  source = s;
  sync();
}
/** Something moved a glass control by an animation of its own (a glide, a width): followed until it rests. */
export function kickGlass(): void {
  if (live) watch();
}

/* ------------------------------------------------------------------ the GPU's glass */

const cap = new Capture();
// (dev scripts: how long the copy of the plan and the drawing of the glass take, frame by frame; which controls have a canvas)
const stats: [number, number][] = [];
if (import.meta.env.DEV) (globalThis as { __glass?: unknown }).__glass = { stats, panes: () => [...panes.values()].map((p) => ({ cls: p.el.className, gl: !!p.gc, key: p.key })), made: () => made, redraw: () => readTheme() };
let failed = false;
let live = false;
let theme = 0;
/** Which frame of the plan this is (each control takes the plan round it afresh when it changes). */
let scene = 0;

interface Pane {
  el: HTMLElement;
  /** Its canvas, its own for as long as it lives (null: CSS glass). */
  gc: GlassCanvas | null;
  /** What it was last drawn for (its place, size, look and the plan under it). */
  key: string;
  /** The plan round it as last copied, and what for. */
  region: Region | null;
  regionKey: string;
  /** How much its words need the tint thickened over the plan under it now (0–1, eased frame to frame); -1 not yet known. */
  need: number;
  cssW: number;
  cssH: number;
  /** How many times it has been asked to draw in the last moment (see `draw`). */
  burst: number;
  burstAt: number;
  onScroll: () => void;
  wasPositioned: boolean;
}
const panes = new Map<HTMLElement, Pane>();

const reduced = (): boolean => matchMedia('(prefers-reduced-transparency: reduce)').matches || matchMedia('(prefers-contrast: more)').matches;
const allowed = (): boolean => !failed && !reduced() && document.documentElement.dataset.glass !== 'css' && typeof WebGL2RenderingContext !== 'undefined';

/** Glass drawn by the GPU when there is a plan under it to draw from and nothing says otherwise; CSS glass else. */
let waiting = 0;
function sync(): void {
  // (a plan still arriving — the zoom into it in the air — is looked at again each frame until it has)
  if (source && !source.ready() && !waiting) waiting = requestAnimationFrame(() => ((waiting = 0), sync()));
  const on = !!source && source.ready() && allowed();
  if (on === live) {
    if (on) refreshGlass();
    return;
  }
  live = on;
  if (on) {
    scene++;
    for (const el of tracked) {
      if (el.isConnected && el.offsetWidth) fadeIn.add(el);
      attach(el);
    }
    drawAll();
  } else
    for (const [el, p] of [...panes]) {
      panes.delete(el);
      resizes.unobserve(el);
      el.removeEventListener('scroll', p.onScroll);
      el.classList.remove('gl');
      if (!p.wasPositioned) el.style.position = '';
      if (p.gc) unmake(p.gc);
      p.gc = null;
    }
}


/* ------------------------------------------------------------ the canvases */

const BUDGET = 14;
let made = 0;
/** A canvas of its own for a control, if one may be made (never more than fourteen at once). */
function make(): GlassCanvas | null {
  if (made >= BUDGET) return null;
  try {
    const g: GlassCanvas = new GlassCanvas(() => lost(g));
    g.canvas.className = 'glass-gl';
    g.canvas.setAttribute('aria-hidden', 'true');
    g.canvas.style.cssText = 'position:absolute;left:0;top:0;z-index:-1;display:block;pointer-events:none;border-radius:inherit';
    made++;
    return g;
  } catch (e) {
    // (no WebGL2 at all: CSS glass from now on)
    if (made === 0) {
      console.warn('glass: CSS only', e);
      failed = true;
      sync();
    }
    return null;
  }
}
/** A canvas let go of for good (its control gone): its context given back at once. */
function unmake(g: GlassCanvas): void {
  g.canvas.remove();
  if (!g.lost) g.dispose();
  made = Math.max(0, made - 1);
}
/** A canvas's context lost (the GPU reset, memory short): its control is CSS glass from now on. */
function lost(g: GlassCanvas): void {
  for (const p of panes.values())
    if (p.gc === g) {
      p.gc = null;
      p.el.classList.remove('gl');
    }
  g.canvas.remove();
  made = Math.max(0, made - 1);
}

/* ------------------------------------------------------------ the controls */

function attach(el: HTMLElement): void {
  if (panes.has(el) || cssOnly.has(el)) return;
  // (its canvas is placed from it: a control that is not placed is placed where it is; and it is a stacking
  // context of its own from the first, so that nothing about its layers changes when its glass is drawn)
  const wasPositioned = getComputedStyle(el).position !== 'static';
  if (!wasPositioned) el.style.position = 'relative';
  el.style.isolation = 'isolate';
  const p: Pane = { el, gc: make(), key: '', region: null, regionKey: '', need: -1, cssW: 0, cssH: 0, burst: 0, burstAt: 0, onScroll: () => {}, wasPositioned };
  // its canvas goes in now, once, before anything is drawn or shown
  if (p.gc) el.prepend(p.gc.canvas);
  // a control that scrolls keeps its glass still under what scrolls
  p.onScroll = () => {
    if (p.gc) p.gc.canvas.style.translate = `${el.scrollLeft}px ${el.scrollTop}px`;
  };
  el.addEventListener('scroll', p.onScroll, { passive: true });
  panes.set(el, p);
  resizes.observe(el);
  changes.observe(el, { attributes: true, attributeFilter: ['style', 'class', 'hidden', 'data-kind'], childList: true });
  draw(p);
}
/** A control no longer kept up (gone, or the plan gone from under it): its canvas let go of once it is off the screen. */
function detach(el: HTMLElement): void {
  const p = panes.get(el);
  if (!p) return;
  panes.delete(el);
  resizes.unobserve(el);
  el.removeEventListener('scroll', p.onScroll);
  const g = p.gc;
  p.gc = null;
  if (!g) return;
  // (still on screen — fading out with its screen —: kept as drawn until it has gone)
  const done = () => {
    if (el.isConnected && live) return;
    el.classList.remove('gl');
    if (!p.wasPositioned) el.style.position = '';
    unmake(g);
  };
  if (el.isConnected && live) setTimeout(done, 600);
  else done();
}

const resizes = new ResizeObserver((entries) => {
  const list = entries.map((e) => panes.get(e.target as HTMLElement)).filter((p): p is Pane => !!p);
  if (list.length) update(list);
});
// (anything said of a control — placed, shown, hidden, restyled, its words replaced — is drawn before the screen is)
const changes = new MutationObserver((records) => {
  const seen = new Set<Pane>();
  for (const r of records) {
    const p = panes.get(r.target as HTMLElement);
    if (!p || seen.has(p)) continue;
    // (its own canvas coming or going is no change to it)
    if (r.type === 'childList' && [...r.addedNodes, ...r.removedNodes].every((n) => n instanceof HTMLCanvasElement && n.classList.contains('glass-gl'))) continue;
    seen.add(p);
    // (its contents wiped and its canvas with them — which nothing in the app does: see THE RULE OF THE
    // CANVAS — put back, and said so while developing)
    if (p.gc && p.gc.canvas.parentNode !== p.el) {
      if (import.meta.env.DEV) console.warn('glass: a control\'s canvas was taken out', p.el);
      p.el.prepend(p.gc.canvas);
      p.key = '';
    }
  }
  if (seen.size) update([...seen]);
  watch();
});

function drawAll(): void {
  update([...panes.values()]);
}

/** How a control is glass: the bar of a selection, a menu, the pad and a message are read at length (strong); a sheet is the thickest; Share is the one coloured. */
type Variant = 'regular' | 'strong' | 'sheet' | 'accent' | 'error' | 'success';
function variantOf(el: HTMLElement): Variant {
  if (el.matches('dialog .panel')) return 'sheet';
  if (el.classList.contains('tinted')) return 'accent';
  if (el.id === 'toast') return el.dataset.kind === 'error' ? 'error' : el.dataset.kind === 'success' ? 'success' : 'strong';
  if (el.matches('.context, .menu, .numpad')) return 'strong';
  return 'regular';
}

/** A control's glass drawn, if anything about it has changed. */
function draw(p: Pane): void {
  update([p]);
}

/** What a control needs to be drawn now: its size and place, its look, the plan round it to copy. */
interface Job {
  p: Pane;
  geom: PaneGeom;
  look: GlassLook;
  key: string;
  want: { x: number; y: number; w: number; h: number; s: number };
  regionKey: string;
}

/**
 * Controls' glass drawn, those whose anything has changed: each measured, the plan round all those that
 * have moved (or all, a new frame of the plan) copied at once — one copy, one reading — then each drawn.
 */
function update(list: Pane[]): void {
  if (!live || !source) return;
  const jobs: Job[] = [];
  for (const p of list) {
    const j = measure(p);
    if (j) jobs.push(j);
  }
  if (!jobs.length) return;
  const fresh = jobs.filter((j) => !j.p.region || j.p.regionKey !== j.regionKey);
  if (fresh.length) {
    // (clear glass a texel a CSS px, frosted a texel to two: and never more than about a million texels in all)
    const total = fresh.reduce((a, j) => a + j.want.w * j.want.h * j.want.s * j.want.s, 0);
    const fit = Math.min(1, Math.sqrt(1_000_000 / Math.max(1, total)));
    const layers = source.layers().map((canvas) => ({ canvas, rect: canvas.getBoundingClientRect() }));
    const regions = cap.take(layers, source.backdrop(), fresh.map((j) => ({ ...j.want, s: j.want.s * fit })));
    const { dark } = appearance();
    fresh.forEach((j, i) => {
      j.p.region = regions[i]!;
      j.p.regionKey = j.regionKey;
      // (one amount for the whole control, eased: never a patch of thicker tint, never a flicker as the plan moves)
      const want = legibility(j.p.region, j.geom, dark);
      j.p.need = j.p.need < 0 ? want : j.p.need + (want - j.p.need) * 0.3;
    });
  }
  for (const j of jobs) paint(j);
}

/** A control measured: null if there is nothing to draw (no canvas, not laid out, or nothing changed). */
function measure(p: Pane): Job | null {
  // (never more than a few times between two frames, whatever asks: anything that would ask again and again
  // — an observer answering its own change — waits for the next frame instead of holding the page)
  const now = performance.now();
  if (now - p.burstAt > 4) {
    p.burstAt = now;
    p.burst = 0;
  }
  if (++p.burst > 6) {
    watch();
    return null;
  }
  const el = p.el;
  if (!p.gc) return null;
  const w = el.isConnected && !el.hidden ? el.offsetWidth : 0,
    h = w ? el.offsetHeight : 0;
  // not laid out (hidden): nothing to draw — its canvas stays, and is drawn the moment it is laid out. A control
  // laid out but not yet seen (opacity 0, about to fade in) IS drawn: its glass is there before its first frame
  if (!w || !h) return null;
  const cs = getComputedStyle(el);
  if (cs.display === 'none') return null;
  const r = el.getBoundingClientRect();
  const k = r.width / w;
  const dpr = window.devicePixelRatio || 1;
  const v = variantOf(el);
  let radius = parseFloat(cs.borderTopLeftRadius) || 0;
  if (cs.borderTopLeftRadius.endsWith('%')) radius = (radius / 100) * Math.min(w, h);
  const key = `${scene}|${theme}|${w}x${h}@${dpr}|${r.left.toFixed(2)},${r.top.toFixed(2)}|${k.toFixed(4)}|${radius}|${v}`;
  if (key === p.key) return null;
  const look = lookFor(v, el, cs);
  // the plan round it: as far as its lip bends the world in and its frost spreads, inside the screen
  const m = look.blur > 0.5 ? 72 : 52;
  const vw = innerWidth,
    vh = innerHeight;
  const x0 = Math.max(0, Math.floor(r.left - m)),
    y0 = Math.max(0, Math.floor(r.top - m));
  const x1 = Math.min(vw, Math.ceil(r.right + m)),
    y1 = Math.min(vh, Math.ceil(r.bottom + m));
  if (x1 - x0 < 1 || y1 - y0 < 1) return null;
  const s = look.blur >= 2 ? 0.5 : 1;
  const want = { x: x0, y: y0, w: x1 - x0, h: y1 - y0, s };
  return { p, geom: { w, h, r: radius, dpr, x: r.left, y: r.top, k }, look, key, want, regionKey: `${scene}|${x0},${y0},${want.w},${want.h}|${s}` };
}

/**
 * How much a control's words need its tint thickened over the plan under it: over plain paper, nothing; over
 * ink — lettering, dense line work, a dark mark — the more the darker and busier it is (under dark glass, the
 * lighter and busier). Read from the copy of the plan under the control itself (not its margin), sampled.
 */
function legibility(r: Region, g: PaneGeom, dark: boolean): number {
  const px = cap.pixels;
  const sx = r.tw / Math.max(1, r.w),
    sy = r.th / Math.max(1, r.h);
  const x0 = Math.max(0, Math.floor((g.x - r.x) * sx)),
    y0 = Math.max(0, Math.floor((g.y - r.y) * sy));
  const x1 = Math.min(r.tw, Math.ceil((g.x + g.w * g.k - r.x) * sx)),
    y1 = Math.min(r.th, Math.ceil((g.y + g.h * g.k - r.y) * sy));
  if (x1 <= x0 || y1 <= y0) return 0;
  const step = Math.max(1, Math.round(Math.sqrt(((x1 - x0) * (y1 - y0)) / 600)));
  let n = 0,
    sum = 0,
    sq = 0;
  for (let y = y0; y < y1; y += step)
    for (let x = x0; x < x1; x += step) {
      const i = ((r.ay + y) * cap.width + r.ax + x) * 4;
      const l = (0.2126 * px[i]! + 0.7152 * px[i + 1]! + 0.0722 * px[i + 2]!) / 255;
      sum += l;
      sq += l * l;
      n++;
    }
  if (!n) return 0;
  const mean = sum / n;
  const sd = Math.sqrt(Math.max(0, sq / n - mean * mean));
  return Math.max(0, Math.min(1, dark ? (mean - 0.3) * 1.2 + sd * 1.6 : (0.85 - mean) * 1.4 + sd * 1.8));
}

function paint(j: Job): void {
  const { p } = j;
  const g = p.gc;
  if (!g || !p.region) return;
  const el = p.el;
  p.key = j.key;
  // (said only when it changes: a style set is a style to work out again, for every control after it)
  if (p.cssW !== j.geom.w || p.cssH !== j.geom.h) {
    p.cssW = j.geom.w;
    p.cssH = j.geom.h;
    g.canvas.style.width = `${j.geom.w}px`;
    g.canvas.style.height = `${j.geom.h}px`;
  }
  const look = j.look.adapt && p.need > 0 ? { ...j.look, tint: [j.look.tint[0], j.look.tint[1], j.look.tint[2], Math.min(0.92, j.look.tint[3] + j.look.adapt * p.need)] as GlassLook['tint'] } : j.look;
  g.draw(cap, p.region, j.geom, look, { w: innerWidth, h: innerHeight });
  if (el.classList.contains('gl')) return;
  // a control already on screen as CSS glass (the plan just arrived) takes its drawn glass over unseen: the
  // canvas comes in over the CSS glass, which then goes — never a jump from one to the other
  if (fadeIn.has(el) && !matchMedia('(prefers-reduced-motion: reduce)').matches) {
    fadeIn.delete(el);
    const a = g.canvas.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 260, easing: 'ease-out' });
    a.onfinish = a.oncancel = () => void (p.gc === g && el.classList.add('gl'));
    return;
  }
  el.classList.add('gl');
}
/** Controls on screen when the drawn glass begins: theirs fades in over their CSS glass. */
const fadeIn = new WeakSet<HTMLElement>();

/* ------------------------------------------------------------ following what moves */

let watching = 0;
let still = 0;
/** Every frame while a glass control is under way (an animation of its own, or a place that keeps changing), its glass follows it. */
function watch(): void {
  still = 0;
  if (watching || !live) return;
  const tick = () => {
    watching = 0;
    if (!live) return;
    const all = [...panes.values()].filter((p) => p.el.isConnected);
    const before = all.map((p) => p.key);
    update(all);
    const moving = all.some((p, i) => p.key !== before[i] || p.el.getAnimations().some((a) => a.playState === 'running'));
    still = moving ? 0 : still + 1;
    if (still < 3) watching = requestAnimationFrame(tick);
  };
  watching = requestAnimationFrame(tick);
}
// a transition or an animation begun anywhere may be moving a glass control (or what holds it)
for (const t of ['transitionrun', 'animationstart', 'transitionend', 'animationend', 'transitioncancel']) document.addEventListener(t, () => kickGlass(), true);
addEventListener('resize', () => {
  if (live) refreshGlass();
});
window.visualViewport?.addEventListener('resize', () => kickGlass());

/* ------------------------------------------------------------ the look */

const looks = new Map<string, GlassLook>();
/** The tints are the app's (app.css): read again when the appearance changes. */
function readTheme(): void {
  theme++;
  looks.clear();
  if (live) drawAll();
}
new MutationObserver(readTheme).observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
matchMedia('(prefers-color-scheme: dark)').addEventListener('change', readTheme);
for (const q of ['(prefers-reduced-transparency: reduce)', '(prefers-contrast: more)']) matchMedia(q).addEventListener('change', () => sync());

/** A colour as the browser resolves it (any CSS colour), as 0–1 rgb and alpha. */
const probe = document.createElement('span');
function rgba(css: string): [number, number, number, number] {
  probe.style.color = '';
  probe.style.color = css;
  if (!probe.isConnected) {
    probe.style.display = 'none';
    document.documentElement.append(probe);
  }
  const c = getComputedStyle(probe).color;
  // (rgb() in 0–255; a mixed colour may come back as color(srgb …) in 0–1)
  const srgb = c.match(/color\(srgb ([^)]+)\)/);
  if (srgb) {
    const [r = 1, g = 1, b = 1, a = 1] = srgb[1]!.split(/[ ,/]+/).filter(Boolean).map(Number);
    return [r, g, b, a];
  }
  const m = c.match(/rgba?\(([^)]+)\)/);
  const [r = 255, g = 255, b = 255, a = 1] = m ? m[1]!.split(/[ ,/]+/).filter(Boolean).map(Number) : [];
  return [r / 255, g / 255, b / 255, a];
}

/** The appearance, worked out once for each change of it (never once per control drawn: reading a colour restyles the page). */
let themed: { at: number; root: CSSStyleDeclaration; dark: boolean; accent: string } | null = null;
function appearance(): { root: CSSStyleDeclaration; dark: boolean; accent: string } {
  if (themed && themed.at === theme) return themed;
  const root = getComputedStyle(document.documentElement);
  const dark = rgba(root.getPropertyValue('--bg').trim() || '#fff').slice(0, 3).reduce((a, c) => a + c, 0) < 1.2;
  themed = { at: theme, root, dark, accent: root.getPropertyValue('--accent').trim() };
  return themed;
}
function lookFor(v: Variant, el: HTMLElement, cs: CSSStyleDeclaration): GlassLook {
  const { root, dark, accent: rootAccent } = appearance();
  const accent = v === 'accent' ? cs.getPropertyValue('--tint').trim() || rootAccent : '';
  // (in a sheet — the sheet itself, the pad beside it — the plan behind is dimmed by the sheet's backdrop)
  const inSheet = !!el.closest('dialog');
  const cacheKey = `${v}|${dark}|${accent}|${inSheet}`;
  const had = looks.get(cacheKey);
  if (had) return had;
  const css = (name: string) => rgba(root.getPropertyValue(name).trim() || 'transparent');
  // As Apple draws it (2026-10-24, Adam, beside macOS 27's Control Center: "DO IT LIKE THAT"): a clear slab
  // lying on the plan — flat, evenly frosted, never magnified, a touch deeper than what is under it — its
  // edge one crisp line of light (brightest along the top and the foot, dimmer round the ends), and only
  // over a narrow bevel the world just outside bent in. Nothing else: no frame, no glow, no shaded band.
  const light = dark ? { rimTop: 0.42, rimFoot: 0.3, sideLine: 0.16 } : { rimTop: 1, rimFoot: 0.9, sideLine: 0.6 };
  const lens = { refract: 0.9, curve: 2.2, disperse: 0.03, bezel: 0.14 };
  let look: GlassLook;
  switch (v) {
    case 'strong': {
      // (read at length — a menu, the bar by a selection, the pad: more frost and tint, still plainly glass)
      const t = css('--glass-strong');
      look = { tint: [t[0], t[1], t[2], dark ? t[3] * 0.82 : 0.5], sat: 1.4, bright: dark ? 0.8 : 0.97, blur: 1.6, dim: 1, ...light, ...lens, adapt: 0.25 };
      break;
    }
    case 'sheet': {
      const t = css('--sheet');
      look = { tint: [t[0], t[1], t[2], 0.78], sat: 1.6, bright: 1, blur: 2.4, dim: 1, ...light, ...lens, refract: 0.5, bezel: 0.04, adapt: 0.1 };
      break;
    }
    case 'accent': {
      const t = rgba(accent || '#007aff');
      look = { tint: [t[0], t[1], t[2], 0.86], sat: 1.2, bright: 1, blur: 1, dim: 1, rimTop: 0.6, rimFoot: 0.4, sideLine: 0.2, ...lens, adapt: 0 };
      break;
    }
    case 'error':
    case 'success': {
      const t = css(v === 'error' ? '--danger' : '--success');
      look = { tint: [t[0], t[1], t[2], 0.86], sat: 1.2, bright: 1, blur: 1.6, dim: 1, rimTop: 0.6, rimFoot: 0.4, sideLine: 0.2, ...lens, adapt: 0 };
      break;
    }
    default: {
      const t = css('--glass');
      look = { tint: [t[0], t[1], t[2], dark ? t[3] * 0.62 : 0.1], sat: 1.35, bright: dark ? 0.84 : 0.91, blur: 0.9, dim: 1, ...light, ...lens, adapt: dark ? 0.32 : 0.45 };
    }
  }
  if (inSheet) look = { ...look, dim: 0.68 };
  // (dev: the look tried out by a script — `__glassTune = { all: {…}, regular: {…} }`, then `__glass.redraw()`)
  if (import.meta.env.DEV) {
    const tune = (globalThis as { __glassTune?: Record<string, Partial<GlassLook>> }).__glassTune;
    if (tune) look = { ...look, ...tune.all, ...tune[v] };
  }
  looks.set(cacheKey, look);
  return look;
}

